import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Settings → Signed-in devices (WS-Q Q-5).
 *
 * Until this existed the only revocation anyone could reach was "Sign out", on the device in
 * front of them. A laptop left at a client's office, or a session on a machine that was sold,
 * could not be ended at all — the chain simply lived out its days. This list is what makes
 * "Keep me signed in on this device" (D-35) safe to offer: the longer a credential lives, the
 * more its owner needs a way to end it from somewhere else.
 *
 * A row is a session CHAIN, not a token: one device's continuous session from login to
 * logout or expiry. Ending a row ends that device, and nothing else.
 *
 * Renders NOTHING when the cloud has no `/api/auth/sessions` (an older deployment), so a
 * mixed fleet degrades to today's behaviour instead of showing an empty, broken block.
 * Adds no CSS rule — WS-K1/K2 own the stylesheets; this reuses the settings classes.
 */
import { useCallback, useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import { signOutEverywhere } from "../app/signOut.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { InfoHint } from "../ui/Tooltip.js";
import { parseServerTs, serverTsDate, type SignedInDevice } from "@genz/platform";
import { RowAction } from "../ui/RowAction.js";

/** "3h ago" / "2d ago" — through `parseServerTs`, so a `Z` timestamp is not read as local. */
function lastUsed(iso: string | null): string {
  const t = parseServerTs(iso);
  if (!t) return "never used";
  const mins = Math.max(0, (Date.now() - t) / 60000);
  if (mins < 5) return "in use now";
  if (mins < 60) return `${Math.round(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.round(hrs)}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function expiry(iso: string | null): string {
  const d = serverTsDate(iso);
  return d ? `signs out ${d.toLocaleDateString()}` : "no expiry recorded";
}

export function SessionsPanel({ active = true, onRowCount }: {
  /** WS-Z4: false while another Settings section is open. The list still MOUNTS and still
   *  loads — `onRowCount` is what puts its entry in the sub-nav at all — and simply draws
   *  nothing until it is the open section. */
  active?: boolean;
  /** How many signed-in devices this account has (0 on a cloud with no sessions route). */
  onRowCount?: (n: number) => void;
} = {}) {
  const services = useServices();
  const { client } = services;
  const [rows, setRows] = useState<SignedInDevice[] | null>(null);
  const [supported, setSupported] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await client.listSessions());
    } catch {
      // An older cloud has no such route. Hide the block rather than show an empty one —
      // "you have no signed-in devices" would be a lie on the very device reading it.
      setSupported(false);
    }
  }, [client]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    onRowCount?.(supported && rows ? rows.length : 0);
  }, [onRowCount, supported, rows]);

  /**
   * The question, per row. Ending ANOTHER device and ending THIS one are different acts and
   * do not share a sentence — the second one signs the operator out where they are standing.
   */
  function question(row: SignedInDevice): string {
    const name = row.label || "this unnamed device";
    return row.current
      ? `End the session on ${name}? This is the device you are using — you will be signed out here.`
      : `End the session on ${name}? That device will have to sign in again.`;
  }

  async function revoke(row: SignedInDevice) {
    setBusy(row.chain_id);
    setError(null);
    try {
      await client.revokeSession(row.chain_id);
      if (row.current) {
        // Ending THIS device is a deliberate sign-out, and the row's own question promises it
        // ("you will be signed out here"). Doing only the server half left the operator in the
        // app until the next request 401'd (the server refuses an ended chain's access token)
        // and dropped them at the EXPIRY prompt instead — a sign-out they chose, presented as
        // one that happened to them, with this account's content still on a machine they may
        // be handing on.
        //
        // Server FIRST, exactly as the Sign out button does (`App.tsx`): step 1 of the purge
        // clears the very token the revoke has to present. `reason: "user"` is what asks for
        // the content tier as well as the identity one.
        await signOutEverywhere({
          io: services.io,
          engine: services.engine,
          log: (level, message, ctx) => services.logger?.log(level, message, ctx),
          // H-2: the COMPOSED purge (telemetry + client log), not the logger alone.
          purgeBuffers: () => services.purgeAccountBuffers(),
        }, { reason: "user" });
        return; // there is no list left to re-read — the token it would need is gone
      }
      await load();
    } catch (e) {
      // LOUD: a device that was NOT signed out must never look as though it was.
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (!active || !supported || !rows || rows.length === 0) return null;

  return (
    <section data-testid="sessions-panel">
      {/* WS-Z4 / rule 10: the standing explanation is an `InfoHint` on the heading it
          qualifies; the FAILURE below it stays on the canvas, as a warn banner, because
          "that device was not signed out" is an alert and not an explanation (rule 9). */}
      <h3>
        Signed-in devices{" "}
        <span data-testid="sessions-note">
          <InfoHint
            label="What this list shows"
            text="Every device currently signed in to this account. Ending one signs out that device only — the others keep working."
          />
        </span>
      </h3>
      {error && (
        <p role="alert" className="banner banner--warn" data-testid="sessions-error">
          <ErrorNotice detail={error} />
        </p>
      )}
      <ul className="settings__preset-list">
        {rows.map((row) => (
          <li
            key={row.chain_id}
            data-testid={`session-${row.chain_id}`}
            data-current={row.current ? "true" : "false"}
          >
            <span className="connectors__name">{row.label || "Unnamed device"}</span>
            {row.current && <span className="badge">This device</span>}
            {row.persistent && <span className="badge">Stays signed in</span>}
            <span className="faint">{lastUsed(row.last_used_at)} · {expiry(row.expires_at)}</span>
            {/*
              An in-app confirm and not `window.confirm`: `ConfirmPopover` exists precisely
              because the native dialog is Tauri-flaky, and a falsy return there would make
              "Sign out this device" silently do nothing on the desktop — the one platform
              this list is most for.
            */}
            <ConfirmPopover
              message={question(row)}
              confirmLabel={row.current ? "Sign out here" : "End that session"}
              danger
              onConfirm={() => void revoke(row)}
            >
              {(toggle) => (
                <RowAction
                  label={busy === row.chain_id ? "Ending…" : "Sign out this device"}
                  icon="power"
                  tone="quiet"
                  disabled={busy === row.chain_id}
                  busy={busy === row.chain_id}
                  data-testid={`session-revoke-${row.chain_id}`}
                  onClick={toggle}
                />
              )}
            </ConfirmPopover>
          </li>
        ))}
      </ul>
    </section>
  );
}
